import { useEffect, useState } from 'react';
import { SectionCard } from '../components/common/SectionCard';
import EquityCurveChart from '../components/performance/EquityCurveChart';
import DrawdownChart from '../components/performance/DrawdownChart';
import MonthlyReturnsChart from '../components/performance/MonthlyReturnsChart';
import MarketScopeTabs from '../components/market/MarketScopeTabs';
import { useCrossMarketStatus } from '../hooks/useCrossMarketStatus';
import type { CrossMarketStatus } from '../services/crossMarketApi';
import {
  fetchOperatingPerformance,
  OperatingPerformanceData,
} from '../services/operatingPerformanceApi';
import {
  BreakdownTable,
  PositionsTable,
  TradesTable,
} from '../components/operating/OperatingPerformanceTables';
import {
  KrPerformanceEmpty,
  Metric,
  UnavailablePerformance,
  UsLivePerformanceEmpty,
} from '../components/operating/OperatingPerformanceStates';
import './OperatingPerformance.css';
import '../components/performance/PerformanceLayout.css';
import '../components/market/MarketScope.css';
import type { MarketScope } from '../types/marketScope';

const moneyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export default function OperatingPerformance() {
  const [data, setData] = useState<OperatingPerformanceData | null>(null);
  const [scope, setScope] = useState<MarketScope>('US');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const marketStatus = useCrossMarketStatus();

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchOperatingPerformance());
    } catch (err) {
      setError(err instanceof Error ? err.message : '운영 성과 조회 실패');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading || (marketStatus.loading && !marketStatus.data)) {
    return <div className="operating-page"><div className="state-box">로딩 중...</div></div>;
  }

  const usLive = isUsLivePilot(marketStatus.data);
  if ((error || !data) && !usLive) {
    return (
      <div className="operating-page">
        <div className="state-box error-state">API 오류: {error}</div>
        <button className="op-refresh" onClick={load}>다시 시도</button>
      </div>
    );
  }

  return (
    <div className="operating-page">
      <header className="op-header">
        <div>
          <h1>운영 성과</h1>
          <p>US live_pilot 성과는 이전 US 운영 리포트와 분리해서 봅니다.</p>
        </div>
        <button className="op-refresh" onClick={load}>새로고침</button>
      </header>
      <MarketScopeTabs value={scope} onChange={setScope} status={marketStatus.data} />
      {scope === 'KR' ? (
        <KrPerformanceEmpty />
      ) : usLive ? (
        <UsLivePerformanceEmpty />
      ) : !data ? (
        <UnavailablePerformance error={error} />
      ) : (
        <UsPerformance data={data} />
      )}
    </div>
  );
}

function UsPerformance({ data }: { data: OperatingPerformanceData }) {
  return (
    <>
      <p className="op-scope-note">
        {data.range.start} ~ {data.range.end} · {data.range.days}개 이전 US 운영 리포트 기준
      </p>
      <section className="op-metrics-grid">
        <Metric title="총 자산" value={moneyFormatter.format(data.summary.totalValue)} />
        <Metric title="총 수익률" value={`${data.summary.totalReturn.toFixed(2)}%`} tone={data.summary.totalReturn >= 0 ? 'good' : 'bad'} />
        <Metric title="최대 낙폭" value={`${data.summary.maxDrawdown.toFixed(2)}%`} tone="warn" />
        <Metric title="실현 손익" value={moneyFormatter.format(data.summary.realizedProfit)} tone={data.summary.realizedProfit >= 0 ? 'good' : 'bad'} />
        <Metric title="미실현 손익" value={moneyFormatter.format(data.summary.unrealizedProfit)} tone={data.summary.unrealizedProfit >= 0 ? 'good' : 'bad'} />
        <Metric title="완료 거래 승률" value={`${data.summary.winRate.toFixed(1)}%`} />
      </section>

      <section className="op-chart-grid">
        <EquityCurveChart data={data.equityCurve} />
        <DrawdownChart data={data.drawdownSeries} />
        <MonthlyReturnsChart data={data.monthlyReturns} />
      </section>

      <section className="op-grid">
        <SectionCard title="현재 포지션" subtitle={`${data.summary.openPositions}개 보유`}>
          <PositionsTable positions={data.positions} />
        </SectionCard>
        <SectionCard title="실현 손익 분포" subtitle={`${data.summary.tradeCount}건 기준`}>
          <BreakdownTable rows={data.tradeBreakdown} />
        </SectionCard>
      </section>

      <SectionCard title="최근 실현 거래" subtitle="익절, 손절, 전략 매도 기록">
        <TradesTable trades={data.trades} />
      </SectionCard>
    </>
  );
}

function isUsLivePilot(status: CrossMarketStatus | null): boolean {
  const safety = status?.safety;
  return !!safety && safety.trading_mode === 'live' && safety.live_trading_enabled
    && safety.toss_live_market === 'US' && safety.toss_us_live_enabled;
}
